import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useBranding } from 'src/branding/BrandingContextProvider';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  convertHTMLToString,
  getTenantName,
} from 'src/helpers/validationHelper';
import { upload } from 'src/helpers/upLoadHelper';
import { usePropertiesAPI } from 'src/SharedCustomHooks/UsePropertiesAPI/usePropertiesAPI';
import {
  FetchAnnouncementOptionsResult,
  SaveAnnouncementResult,
  useAnnouncementAPI,
} from './useAnnouncementAPI';
import { useAnnouncementFormUIState } from './useAnnouncementFormUIState';
import {
  AnnouncementFormData,
  useAnnouncementFormEntityState,
} from './useAnnouncementFormEntityState';

export type AnnouncementSaveAction = 'SaveAndClose' | 'SaveAndAdd';

export const MAX_ATTACHMENTS: number = 5;

export interface AnnouncementPropertyOption {
  value: string;
  label: string;
}

export interface EmptyScopeWarning {
  title: string;
  subtitle: string;
}

interface UseAnnouncementFormOrchestratorParams {
  initialData?: Partial<AnnouncementFormData>;
  onClose: () => void;
  onSaved: () => void;
}

export interface UseAnnouncementFormOrchestratorReturn {
  propertyOptions: AnnouncementPropertyOption[];
  announcementFormData: AnnouncementFormData;
  uiState: ReturnType<typeof useAnnouncementFormUIState>['uiState'];
  savingAction: AnnouncementSaveAction | null;
  canSubmit: boolean;
  emptyScopeWarning: EmptyScopeWarning | null;
  canAddAttachments: boolean;
  sendConfirmationMessage: string;

  buildingSelectAll: boolean;
  floorSelectAll: boolean;
  unitSelectAll: boolean;

  onTitleChange: (value: string) => void;
  onEditorChange: (editorState: any) => void;
  onPropertyChange: (propertyId: string) => Promise<void>;
  onPlanChange: (plan: string) => void;

  onBuildingsChange: (selectedList: any) => Promise<void>;
  onFloorChange: (selectedList: any) => Promise<void>;
  onUnitsChange: (selectedList: any) => void;

  onSelectAllBuildings: () => Promise<void>;
  onSelectAllFloors: () => Promise<void>;
  onSelectAllUnits: () => void;

  onSelectAttachments: (files: any) => void;
  onDeleteAttachment: (item: any) => void;

  onSave: (action: AnnouncementSaveAction) => Promise<void>;
}

const uploadAttachments = async (attachments: any[]): Promise<any[]> => {
  if (!attachments || attachments.length === 0) {
    return [];
  }

  const uploaded: any[] = [];

  for (const item of attachments) {
    if (item && item.id) {
      uploaded.push(item);
    } else {
      // Todo: Awh Geez this is pretty bad: https://app.asana.com/1/41213529744312/project/1208712340140532/task/1215446161793883?focus=true
      const imageUrl: string = await upload(item);
      uploaded.push({ title: imageUrl.split('.com/')[1], url: imageUrl });
    }
  }

  return uploaded;
};

const buildSaveParams = (
  formData: AnnouncementFormData,
  userId: string,
  uploadedAttachments: any[]
) => ({
  title: formData.title,
  description: formData.description,
  userId,
  propertyId: formData.propertyId,
  attachments:
    uploadedAttachments.length > 0 ? JSON.stringify(uploadedAttachments) : '',
  IsBuilding: formData.IsBuilding,
  IsLevel: formData.IsLevel,
  AnnouncementBuildingLevels: formData.AnnouncementBuildingLevels,
  AnnouncementSuites: formData.AnnouncementSuites,
  tenant: getTenantName(),
});

const buildEmptyScopeWarning = (
  missingBuildings: boolean,
  missingLevels: boolean
): EmptyScopeWarning => {
  const missingParts: string[] = [];

  if (missingBuildings) {
    missingParts.push('buildings');
  }
  if (missingLevels) {
    missingParts.push('floors/levels');
  }

  const missingLabel = missingParts.join(' or ');

  return {
    title: `This property has no ${missingLabel} set up`,
    subtitle: `There's nothing to send this announcement to yet — add ${missingLabel} to the property, then come back.`,
  };
};

const buildRecipientScope = (
  suiteTerm: string,
  selectedUnitsCount: number,
  isBuilding: boolean,
  selectedBuildingsCount: number,
  isLevel: boolean,
  selectedFloorsCount: number
): string => {
  const singular: string = suiteTerm.toLowerCase();
  const plural: string = `${singular}s`;

  if (selectedUnitsCount > 0) {
    return `the ${selectedUnitsCount} selected ${plural}`;
  }

  if (isBuilding && selectedBuildingsCount > 0) {
    return `every ${singular} in the selected building(s)`;
  }

  if (isLevel && selectedFloorsCount > 0) {
    return `every ${singular} on the selected floor(s)`;
  }

  return `every ${plural}`;
};

const toBuildingLevels = (list: any[]) =>
  list.map((item: any) => ({ BuildingLevelId: item.id }));

const toAnnouncementSuites = (list: any[]) =>
  list.map((item: any) => ({ UnitId: item.id }));

export const useAnnouncementFormOrchestrator = ({
  initialData,
  onClose,
  onSaved,
}: UseAnnouncementFormOrchestratorParams): UseAnnouncementFormOrchestratorReturn => {
  const user: any = useSelector(selectAuthenticatedUser);
  const { branding } = useBranding();

  const { fetchBuildings, fetchLevels, fetchUnits, saveAnnouncement } =
    useAnnouncementAPI();

  const { properties, fetchProperties } = usePropertiesAPI();

  const { uiState, patchUiState, remountMultiselects } =
    useAnnouncementFormUIState({
      description: initialData?.description,
    });

  const {
    announcementFormData,
    updateAnnouncementFormData,
    resetAnnouncementFormData,
  } = useAnnouncementFormEntityState(initialData);

  const [savingAction, setSavingAction] =
    useState<AnnouncementSaveAction | null>(null);

  const propertyOptions: AnnouncementPropertyOption[] = properties.map(
    (property: any) => ({
      value: property.propertyId,
      label: property.name,
    })
  );

  const buildingSelectAll: boolean =
    uiState.buildingOptions.length > 0 &&
    uiState.selectedBuildings.length === uiState.buildingOptions.length;

  const floorSelectAll: boolean =
    uiState.levelsOptions.length > 0 &&
    (uiState.selectedFloorValues?.length ?? 0) === uiState.levelsOptions.length;

  const unitSelectAll: boolean =
    uiState.unitOptions.length > 0 &&
    uiState.selectedUnitValues.length === uiState.unitOptions.length;

  const hydrateForProperty = async (propertyId: string) => {
    const [buildingsResult, levelsResult] = await Promise.all([
      fetchBuildings(propertyId),
      fetchLevels(propertyId),
    ]);

    patchUiState({
      buildingOptions: buildingsResult.options,
      levelsOptions: levelsResult.options,
      hydratedPropertyId: propertyId,
    });
  };

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    void fetchProperties(user.id, user.roles[0].role.name);
  }, []);

  useEffect(() => {
    if (!announcementFormData.propertyId) {
      return;
    }

    void hydrateForProperty(announcementFormData.propertyId);
  }, []);

  const onTitleChange = (value: string) => {
    updateAnnouncementFormData({ title: value });
  };

  const onEditorChange = (editorState: any) => {
    patchUiState({ description: editorState });
    updateAnnouncementFormData({ description: editorState });
  };

  const onPropertyChange = async (propertyId: string) => {
    updateAnnouncementFormData({ propertyId });
    patchUiState({
      selectedBuildings: [],
      selectedFloorValues: [],
      selectedUnitValues: [],
      hydratedPropertyId: null,
    });
    remountMultiselects({ building: true, floor: true, unit: true });

    await hydrateForProperty(propertyId);
  };

  const onPlanChange = (plan: string) => {
    patchUiState({ plan });
  };

  const applyBuildingScope = async (selectedList: any) => {
    if (selectedList.length === 0) {
      updateAnnouncementFormData({
        IsBuilding: true,
        IsLevel: false,
        AnnouncementBuildingLevels: [],
        AnnouncementSuites: [],
      });
      patchUiState({ unitOptions: [], selectedUnitValues: [] });
      remountMultiselects({ unit: true });

      return;
    }

    const scopeIds = selectedList.map((item: any) => item.id);

    const unitsResult: FetchAnnouncementOptionsResult = await fetchUnits(
      announcementFormData.propertyId as string,
      'Building',
      scopeIds
    );

    if (!unitsResult.ok) {
      return;
    }

    const buildingLevels = toBuildingLevels(selectedList);

    updateAnnouncementFormData({
      IsBuilding: true,
      IsLevel: false,
      AnnouncementBuildingLevels: buildingLevels,
      AnnouncementSuites: [],
    });

    patchUiState({ unitOptions: unitsResult.options, selectedUnitValues: [] });
    remountMultiselects({ unit: true });
  };

  const applyFloorScope = async (selectedList: any) => {
    if (selectedList.length === 0) {
      updateAnnouncementFormData({
        IsBuilding: false,
        IsLevel: true,
        AnnouncementBuildingLevels: [],
        AnnouncementSuites: [],
      });
      patchUiState({ unitOptions: [], selectedUnitValues: [] });
      remountMultiselects({ unit: true });

      return;
    }

    const scopeIds = selectedList.map((item: any) => item.id);

    const unitsResult: FetchAnnouncementOptionsResult = await fetchUnits(
      announcementFormData.propertyId as string,
      'Levels',
      scopeIds
    );

    if (!unitsResult.ok) {
      return;
    }

    const buildingLevels = toBuildingLevels(selectedList);

    updateAnnouncementFormData({
      IsBuilding: false,
      IsLevel: true,
      AnnouncementBuildingLevels: buildingLevels,
      AnnouncementSuites: [],
    });

    patchUiState({ unitOptions: unitsResult.options, selectedUnitValues: [] });
    remountMultiselects({ unit: true });
  };

  const onBuildingsChange = async (selectedList: any) => {
    patchUiState({ selectedBuildings: selectedList });

    await applyBuildingScope(selectedList);
  };

  const onFloorChange = async (selectedList: any) => {
    patchUiState({ selectedFloorValues: selectedList });

    await applyFloorScope(selectedList);
  };

  const onUnitsChange = (selectedList: any) => {
    const announcementSuites = toAnnouncementSuites(selectedList);

    patchUiState({ selectedUnitValues: selectedList });
    updateAnnouncementFormData({ AnnouncementSuites: announcementSuites });
  };

  const onSelectAllBuildings = async () => {
    const next = buildingSelectAll ? [] : [...uiState.buildingOptions];

    await onBuildingsChange(next);
    remountMultiselects({ building: true });
  };

  const onSelectAllFloors = async () => {
    const next = floorSelectAll ? [] : [...uiState.levelsOptions];

    await onFloorChange(next);
    remountMultiselects({ floor: true });
  };

  const onSelectAllUnits = () => {
    const next = unitSelectAll ? [] : [...uiState.unitOptions];

    onUnitsChange(next);
    remountMultiselects({ unit: true });
  };

  const onSelectAttachments = (files: any) => {
    const existing: any[] = announcementFormData.attachments || [];

    const remainingSlots: number = MAX_ATTACHMENTS - existing.length;

    if (remainingSlots <= 0) {
      toast.error(
        `🚫 You can attach up to ${MAX_ATTACHMENTS} files per announcement.`
      );

      return;
    }

    const stagedNames: string[] = existing
      .filter((item: any) => !item.id)
      .map((item: any) => item.name);

    const deduped: any[] = files.filter(
      (file: any) => !stagedNames.includes(file.name)
    );

    const accepted: any[] = deduped.slice(0, remainingSlots);

    if (deduped.length > remainingSlots) {
      toast.error(
        `🚫 Only ${remainingSlots} more can be added — the limit is ${MAX_ATTACHMENTS} files per announcement.`
      );
    }

    updateAnnouncementFormData({ attachments: [...accepted, ...existing] });
  };

  const onDeleteAttachment = (item: any) => {
    const existing: any[] = announcementFormData.attachments || [];
    const deletedFiles: any[] = announcementFormData.deletedFiles || [];

    const nextDeleted: any[] = item.id
      ? [{ ...item, isDeleted: true }, ...deletedFiles]
      : deletedFiles;

    const nextAttachments: any[] = existing.filter((data: any) =>
      data && data.id ? data.id !== item.id : data !== item
    );

    updateAnnouncementFormData({
      attachments: nextAttachments,
      deletedFiles: nextDeleted,
    });
  };

  const descriptionText: string = convertHTMLToString(
    announcementFormData.description
  );

  const canSubmit: boolean = Boolean(
    announcementFormData.title &&
      announcementFormData.title.trim() &&
      descriptionText &&
      announcementFormData.propertyId
  );

  const optionsHydrated: boolean =
    Boolean(announcementFormData.propertyId) &&
    uiState.hydratedPropertyId === announcementFormData.propertyId;

  const hasNoBuildings: boolean = uiState.buildingOptions.length === 0;
  const hasNoLevels: boolean = uiState.levelsOptions.length === 0;

  const emptyScopeWarning: EmptyScopeWarning | null =
    optionsHydrated && hasNoBuildings && hasNoLevels
      ? buildEmptyScopeWarning(hasNoBuildings, hasNoLevels)
      : null;

  const canAddAttachments: boolean =
    (announcementFormData.attachments || []).length < MAX_ATTACHMENTS;

  const selectedPropertyName: string =
    propertyOptions.find(
      (option: AnnouncementPropertyOption) =>
        option.value === announcementFormData.propertyId
    )?.label ?? 'this property';

  const suiteTerm: string = branding?.text?.suite || 'Suite';

  const recipientScope: string = buildRecipientScope(
    suiteTerm,
    uiState.selectedUnitValues.length,
    Boolean(announcementFormData.IsBuilding),
    uiState.selectedBuildings.length,
    Boolean(announcementFormData.IsLevel),
    uiState.selectedFloorValues?.length ?? 0
  );

  const sendConfirmationMessage: string = `This will email everyone associated with ${recipientScope} at ${selectedPropertyName}. Announcements send immediately and can't be edited once sent — are you sure you want to send?`;

  const onSave = async (action: AnnouncementSaveAction) => {
    if (!canSubmit) {
      console.warn(
        'AnnouncementForm: onSave called without required fields (title/description/property)'
      );

      return;
    }

    setSavingAction(action);

    const uploadedAttachments: any[] = await uploadAttachments(
      announcementFormData.attachments
    );

    const params: any = buildSaveParams(
      announcementFormData,
      user.id,
      uploadedAttachments
    );

    const result: SaveAnnouncementResult = await saveAnnouncement(params);

    setSavingAction(null);

    if (!result.ok) {
      return;
    }

    toast.success('ⓘ Announcement has been sent.', {
      autoClose: 10000,
      position: 'top-center',
    });

    onSaved();

    if (action === 'SaveAndClose') {
      onClose();

      return;
    }

    resetAnnouncementFormData();

    patchUiState({
      selectedFloorValues: [],
      selectedBuildings: [],
      selectedUnitValues: [],

      plan: 'buildings',
      buildingOptions: [],
      levelsOptions: [],
      unitOptions: [],
      hydratedPropertyId: null,

      description: '',
    });

    remountMultiselects({ building: true, floor: true, unit: true });
  };

  return {
    propertyOptions,
    announcementFormData,
    uiState,
    savingAction,
    canSubmit,
    emptyScopeWarning,
    canAddAttachments,
    sendConfirmationMessage,

    buildingSelectAll,
    floorSelectAll,
    unitSelectAll,

    onTitleChange,
    onEditorChange,
    onPropertyChange,
    onPlanChange,

    onBuildingsChange,
    onFloorChange,
    onUnitsChange,

    onSelectAllBuildings,
    onSelectAllFloors,
    onSelectAllUnits,

    onSelectAttachments,
    onDeleteAttachment,

    onSave,
  };
};
